/**
 * UserBar Component
 * Modern ES6 module for user authentication display and navigation
 *
 * Features:
 * - User authentication state display
 * - Role-based navigation
 * - Organization selector integration
 * - Login/logout functionality
 * - Auto-injection for pages without userbar container
 * - Modern ES6 module structure with proper imports
 *
 * @version 2.1.0
 * @author Zubduq
 */

import UserContextService from '../../services/UserContextService.js';
import AuthGuard from '../../auth/authGuard.js';
import { getIcon, createIcon, ICONS } from '../../constants/icons.js';
import componentLifecycleManager from '../../utils/componentLifecycleManager.js';

/**
 * UserBar Manager Class
 * Handles user authentication state and navigation in the application header
 */
class UserBarManager {
  constructor() {
    // Prevent multiple instances
    if (UserBarManager.instance) {
      console.log('✅ UserBarManager already exists, returning existing instance');
      return UserBarManager.instance;
    }

    console.log('🚀 UserBarManager constructor called');

    this.container = null;
    this.retryCount = 0;
    this.maxRetries = 20; // 1 second max wait time
    this.initialized = false;
    this.setupComplete = false; // Prevent duplicate setup calls
    this.eventsSubscribed = false; // Prevent duplicate event subscriptions
    this.lastRenderedUserKey = null; // Prevent unnecessary re-renders
    this.tokenCheckInterval = null;
    this.userContextService = null;
    this.authGuard = new AuthGuard();

    // Register component with lifecycle manager
    componentLifecycleManager.registerComponent('UserBar', this);
    this.lifecycle = componentLifecycleManager.createMixin('UserBar');

    // Store instance reference
    UserBarManager.instance = this;

    // Bind methods to preserve context
    this.render = this.render.bind(this);
    this.handleLogin = this.handleLogin.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.tryRender = this.tryRender.bind(this);
    this.checkTokenValidity = this.checkTokenValidity.bind(this);
    this.handleTokenExpiry = this.handleTokenExpiry.bind(this);
    this.onUserChanged = this.onUserChanged.bind(this);
    this.onOrganizationChanged = this.onOrganizationChanged.bind(this);
  }

  /**
   * Initialize the UserBar component
   */
  init() {
    if (this.initialized) {
      console.log('🚫 UserBarManager already initialized, skipping');
      return;
    }

    console.log('🚀 UserBarManager init() called');

    // Ensure DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }

    this.startTokenValidityChecks();
    this.initialized = true;

    console.log('✅ UserBarManager initialized');
  }

  /**
   * Setup the UserBar after DOM is ready
   */
  async setup() {
    // Prevent duplicate setup calls
    if (this.setupComplete) {
      console.log('🚫 UserBar setup already completed, skipping');
      return;
    }

    console.log('🚀 UserBar setup() called');

    this.ensureContainer();

    // Initialize or get UserContextService
    await this.initializeUserContextService();

    // Check if we should render the userbar
    if (!this.shouldRenderUserBar()) {
      if (this.container) {
        this.container.style.display = 'none';
      }
      // Even when userbar is not rendered, always render theme button
      this.renderThemeOnlyMode();
    } else {
      this.tryRender();
    }

    // Mark setup as complete regardless of authentication state
    this.setupComplete = true;
    console.log('✅ UserBar setup completed');
  }

  /**
   * Initialize UserContextService and subscribe to events
   */
  async initializeUserContextService() {
    // Prevent duplicate initialization
    if (this.userContextService && this.eventsSubscribed) {
      console.log('🚫 UserContextService already initialized and events subscribed, skipping');
      return;
    }

    try {
      // Get global UserContextService instance or create new one
      if (window.userContextService) {
        // Use existing global instance
        this.userContextService = window.userContextService;
      } else {
        // Create new instance
        this.userContextService = new UserContextService();
      }

      // Wait for service to be ready
      await this.userContextService.waitForReady();

      // Subscribe to events only once
      if (!this.eventsSubscribed) {
        this.userContextService.on('userChanged', this.onUserChanged);
        this.userContextService.on('organizationChanged', this.onOrganizationChanged);
        this.eventsSubscribed = true;
        console.log('📡 UserBar: Subscribed to UserContextService events');
      }

      console.log('✅ UserBar: Connected to UserContextService');
    } catch (error) {
      console.error('UserBar: Failed to initialize UserContextService:', error);
    }
  }

  /**
   * Handle user changed event from UserContextService
   */
  onUserChanged(user) {
    console.log('🔄 UserBar: User changed event received', user);

    // Always re-render when setup is complete
    if (this.setupComplete) {
      if (this.shouldRenderUserBar()) {
        console.log('🎨 UserBar: User changed, triggering render');
        this.render();
      } else {
        console.log('🎨 UserBar: User logged out, rendering theme button');
        if (this.container) {
          this.container.style.display = 'none';
        }
        this.renderThemeOnlyMode();
      }
    } else {
      console.log('🚫 UserBar: Skipping render due to setup incomplete');
    }
  }

  /**
   * Handle organization changed event from UserContextService
   */
  onOrganizationChanged(organization) {
    console.log('🔄 UserBar: Organization changed event received', organization);

    // Only re-render if setup is complete and we're authenticated
    if (this.setupComplete && this.shouldRenderUserBar()) {
      console.log('🎨 UserBar: Organization changed, triggering render');
      this.render();
    } else {
      console.log('🚫 UserBar: Skipping render due to setup incomplete or not authenticated');
    }
  }

  /**
   * Determine if UserBar should be rendered
   * Only render when user is authenticated
   */
  shouldRenderUserBar() {
    const token = localStorage.getItem("jwtToken");

    // Only render UserBar if user is authenticated with a valid token
    if (!token || !this.isValidToken(token)) {
      return false;
    }

    // Additional check using UserContextService
    if (this.userContextService) {
      return this.userContextService.isAuthenticated();
    }

    return true;
  }

  /**
   * Check if JWT token is valid (not expired)
   */
  isValidToken(token) {
    if (!token) return false;

    try {
      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const now = Date.now() / 1000;

      return payload.exp && payload.exp > now;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  /**
   * Start periodic token validity checks
   */
  startTokenValidityChecks() {
    // Check token validity every 30 seconds using lifecycle manager
    this.tokenCheckInterval = this.lifecycle.addInterval(() => {
      this.checkTokenValidity();
    }, 30000, 'Token validity check');

    // Also check on page focus to catch expired tokens when user returns
    this.lifecycle.addEventListener(window, 'focus', this.checkTokenValidity);
  }

  /**
   * Stop token validity checks
   */
  stopTokenValidityChecks() {
    // Lifecycle manager will handle interval cleanup automatically
    // But we can manually stop if needed
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    // Event listeners will be cleaned up by lifecycle manager
  }

  /**
   * Check current token validity and handle expiry
   */
  checkTokenValidity() {
    const token = localStorage.getItem("jwtToken");

    if (!token) {
      // No token found - just clean state silently, don't treat as expiry
      if (this.container) {
        this.container.style.display = 'none';
      }
      return;
    }

    if (!this.isValidToken(token)) {
      // Token exists but is invalid/expired - this IS a real expiry
      this.handleTokenExpiry('Token expired or invalid');
      return;
    }

    // Token is valid - ensure auth guard has latest user info
    this.authGuard.isUserAuthenticated();
  }

  /**
   * Handle token expiry or invalidity
   */
  handleTokenExpiry(reason) {
    console.log('UserBar detected token issue:', reason);

    // Clear authentication data
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("refreshToken");

    // Stop token checks
    this.stopTokenValidityChecks();

    // Hide userbar but show theme button
    if (this.container) {
      this.container.style.display = 'none';
    }
    this.renderThemeOnlyMode();

    // Show user-friendly message
    if (window.showToast) {
      window.showToast('Your session has expired. Please log in again.', 'warning');
    }

    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/auth/login.html';
    }, 2000);
  }

  /**
   * Ensure userbar container exists in the DOM
   */
  ensureContainer() {
    // If we already have a container reference, just verify it's still in DOM
    if (this.container && document.contains(this.container)) {
      console.log('✅ UserBar: Container already exists and is in DOM');
      return;
    }

    console.log('🔍 UserBar: Looking for container in DOM');
    let container = document.getElementById("userbar-container");

    if (!container) {
      console.log('🏗️ UserBar: Creating new container');
      // Auto-inject userbar container if not present
      container = document.createElement("div");
      container.id = "userbar-container";
      container.className = "flex items-center justify-end";

      const header = document.querySelector("header .container");
      if (header) {
        header.appendChild(container);
      } else {
        // Fallback to main header
        const mainHeader = document.querySelector("header");
        if (mainHeader) {
          mainHeader.appendChild(container);
        } else if (document.body) {
          document.body.insertBefore(container, document.body.firstChild);
        }
      }
    } else {
      console.log('✅ UserBar: Found existing container in DOM');
      // Ensure container has proper classes
      if (!container.classList.contains('flex')) {
        container.className = "flex items-center justify-end";
      }
    }

    this.container = container;
  }

  /**
   * Load user information from JWT token - Now uses UserContextService
   */
  async loadUserFromToken() {
    // UserContextService handles token loading and user state
    // Just sync with AuthGuard for backward compatibility
    if (this.userContextService) {
      const user = this.userContextService.getCurrentUser();
      if (user) {
        this.authGuard.currentUser = user;
        this.authGuard.isAuthenticated = true;
      } else {
        this.authGuard.currentUser = null;
        this.authGuard.isAuthenticated = false;
      }
    }
  }

  /**
   * Determine if UserBar should be rendered
   * Only render when user is authenticated
   */
  shouldRenderUserBar() {
    const token = localStorage.getItem("jwtToken");

    // Only render UserBar if user is authenticated with a valid token
    if (!token || !this.isValidToken(token)) {
      return false;
    }

    // Additional check using UserContextService
    if (this.userContextService) {
      return this.userContextService.isAuthenticated();
    }

    // Fallback - check if we have a current user
    const currentUser = this.userContextService ? this.userContextService.getCurrentUser() : null;
    if (!currentUser) {
      return false;
    }

    return true;
  }

  /**
   * Check if JWT token is valid (not expired) - Enhanced with AuthGuard integration
   */
  isValidToken(token) {
    // Use AuthGuard's validation logic for consistency
    return this.authGuard.isValidToken(token);
  }

  /**
   * Try to render the user bar with retry mechanism
   */
  tryRender() {
    if (this.container) {
      this.render();
    } else if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      setTimeout(() => {
        this.ensureContainer();
        this.tryRender();
      }, 50);
    }
    // Silently fail after max retries - component will remain hidden
  }

  /**
   * Render the user bar based on current user state
   */
  render() {
    if (!this.container) {
      return; // Silently return if no container available
    }

    console.log('🎨 UserBar render() called');

    // Double-check if we should render (for dynamic updates)
    if (!this.shouldRenderUserBar()) {
      this.container.style.display = 'none';
      // Always render theme button when not authenticated
      this.renderThemeOnlyMode();
      return;
    }

    this.container.style.display = '';

    // Get current user from UserContextService
    const currentUser = this.userContextService ? this.userContextService.getCurrentUser() : null;

    // Prevent unnecessary re-renders by comparing user state
    const userKey = currentUser ? `${currentUser.id}-${currentUser.name}-${currentUser.role}` : 'no-user';
    if (this.lastRenderedUserKey === userKey) {
      console.log('🚫 UserBar: Same user state, skipping render');
      return;
    }

    console.log('🎨 UserBar: Rendering with user state:', userKey);
    this.lastRenderedUserKey = userKey;

    // Only render if user is authenticated (should always be true at this point)
    if (currentUser) {
      this.renderLoggedIn(currentUser);
    } else {
      // This shouldn't happen due to shouldRenderUserBar() check, but hide as fallback
      this.container.style.display = 'none';
      this.renderThemeOnlyMode();
    }
  }

  /**
   * Render logged out state with professional Outlook styling
   */
  renderLoggedOut() {
    this.container.innerHTML = `
      <button id="userbar-login" class="btn btn--primary btn--sm">
        ${IconManager.get('login', { size: 16 })}
        Sign In
      </button>
    `;

    // Attach event listener
    const loginLink = document.getElementById("userbar-login");
    if (loginLink) {
      loginLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleLogin();
      });
    }
  }

  /**
   * Render logged in state with professional Outlook styling
   */
  renderLoggedIn(user) {
    const initials = this.generateUserInitials(user.name);
    const orgSelector = this.renderOrganizationSelector();
    const userRole = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Employee';

    this.container.innerHTML = `
      <div class="userbar-user-info flex items-center">
        <div class="userbar-avatar">${initials}</div>
        <div class="userbar-details">
          <div class="userbar-name" title="${user.email || ''}">${user.name || "Employee"}</div>
          <div class="userbar-role">${userRole}</div>
        </div>
      </div>
      ${orgSelector}
      <button id="userbar-theme-toggle" class="btn btn--ghost btn--sm btn--mobile-icon-only" title="Toggle theme">
        ${IconManager.get('moon', { size: 20 })}
        <span class="btn__text">Dark</span>
      </button>
      <button id="userbar-logout" class="btn btn--ghost btn--sm btn--mobile-icon-only" title="Sign out">
        ${IconManager.get('logout', { size: 20 })}
        <span class="btn__text">Sign out</span>
      </button>
    `;

    // Attach event listeners
    const logoutLink = document.getElementById("userbar-logout");
    if (logoutLink) {
      logoutLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleLogout();
      });
    }

    // Attach theme toggle event listener
    const themeToggle = document.getElementById("userbar-theme-toggle");
    if (themeToggle) {
      this.setupThemeToggle(themeToggle);
    }

    // Attach organization selector event listeners
    this.attachOrgSelectorListeners();
  }

  /**
   * Render organization selector based on user role
   */
  renderOrganizationSelector() {
    if (!this.userContextService) {
      return ''; // Don't render if no service available
    }

    const currentOrg = this.userContextService.getCurrentOrganization();
    if (!currentOrg) {
      return ''; // Don't render if no organization data
    }

    const currentUser = this.userContextService.getCurrentUser();
    if (currentUser && currentUser.role === 'admin') {
      return this.renderAdminOrgSelector();
    } else {
      return this.renderUserOrgDisplay();
    }
  }

  /**
   * Render admin organization selector (interactive)
   */
  renderAdminOrgSelector() {
    const currentOrg = this.userContextService.getCurrentOrganization();
    const organizations = this.userContextService.getOrganizations();
    const hasMultipleOrgs = organizations && organizations.length > 1;

    return `
      <button id="org-selector-button" class="btn btn--ghost btn--sm" title="${currentOrg.name}">
        ${IconManager.get('organizations', { size: 20 })}
        <span class="userbar-org-text">${currentOrg.name}</span>
        ${hasMultipleOrgs ? IconManager.get('dropdown', { size: 20 }) : ''}
      </button>
    `;
  }

  /**
   * Render user organization display (read-only)
   */
  renderUserOrgDisplay() {
    const currentOrg = this.userContextService.getCurrentOrganization();

    return `
      <div class="userbar-org-display">
        ${IconManager.get('organizations', { size: 20 })}
        <span class="userbar-org-text" title="${currentOrg.name}">${currentOrg.name}</span>
      </div>
    `;
  }

  /**
   * Attach organization selector event listeners
   */
  attachOrgSelectorListeners() {
    const currentUser = this.userContextService.getCurrentUser();
    const organizations = this.userContextService.getOrganizations();

    if (currentUser && currentUser.role === 'admin' && organizations && organizations.length > 1) {
      const orgButton = document.getElementById('org-selector-button');
      if (orgButton) {
        orgButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.showOrgSwitcher();
        });
      }
    }
  }

  /**
   * Show organization switcher modal for admin users
   */
  showOrgSwitcher() {
    const currentOrg = this.userContextService.getCurrentOrganization();
    const organizations = this.userContextService.getOrganizations();

    // Create modal using standard modal structure
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = `
      <div class="modal__backdrop"></div>
      <div class="modal__container modal__container--small">
        <div class="modal__header">
          <h3 class="modal__title">Switch Organization</h3>
          <button class="modal__close" type="button" aria-label="Close">
            ${IconManager.get('close', { size: 16 })}
          </button>
        </div>
        <div class="modal__content">
          ${organizations.map(org => `
            <div class="org-option ${org.id === currentOrg.id ? 'org-option--active' : ''}" data-org-id="${org.id}">
              <div class="org-option__icon">${IconManager.get('organizations', { size: 20 })}</div>
              <span class="org-option__name">${org.name}</span>
              ${org.id === currentOrg.id ? '<span class="org-option__badge">Current</span>' : ''}
            </div>
          `).join('')}
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Show modal with animation
    setTimeout(() => modal.classList.add('show'), 10);

    // Attach modal event listeners
    const closeBtn = modal.querySelector('.modal__close');
    const backdrop = modal.querySelector('.modal__backdrop');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeOrgModal(modal));
    }

    if (backdrop) {
      backdrop.addEventListener('click', () => this.closeOrgModal(modal));
    }

    // Attach organization option listeners
    const orgOptions = modal.querySelectorAll('.org-option');
    orgOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        const orgId = option.dataset.orgId;
        this.switchOrganization(orgId);
        this.closeOrgModal(modal);
      });
    });
  }

  /**
   * Close organization modal
   */
  closeOrgModal(modal) {
    if (modal && modal.parentNode) {
      // Remove show class to trigger exit animation
      modal.classList.remove('show');

      // Remove modal from DOM after animation completes
      setTimeout(() => {
        if (modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      }, 300); // Match the CSS transition duration
    }
  }

  /**
   * Switch to different organization
   */
  switchOrganization(orgId) {
    if (!this.userContextService) {
      console.error('UserContextService not available for organization switching');
      return;
    }

    // Use UserContextService to handle organization switching
    this.userContextService.switchOrganization(orgId);
  }

  /**
   * Generate user initials for avatar
   */
  generateUserInitials(name) {
    if (!name) return 'E';

    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    } else {
      return names[0].charAt(0).toUpperCase();
    }
  }

  /**
   * Handle login button click
   */
  handleLogin() {
    const loginPath = './auth/login.html';
    window.location.href = loginPath;
  }

  /**
   * Handle logout button click - Enhanced with better cleanup
   */
  handleLogout() {
    // Stop token validity checks
    this.stopTokenValidityChecks();

    // Clear authentication
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("refreshToken");

    // Clear auth guard state
    this.authGuard.currentUser = null;
    this.authGuard.isAuthenticated = false;

    // UserContextService handles its own cache cleanup on logout

    // Show success message if toast is available
    if (typeof showSuccess === 'function') {
      showSuccess('Logged out successfully');
    } else if (window.showToast) {
      window.showToast('Logged out successfully', 'success');
    }

    // Redirect to home page
    const homePath = this.getHomePath();
    setTimeout(() => {
      window.location.href = homePath;
    }, 500); // Small delay to show toast message
  }

  /**
   * Get path to home page based on current location
   */
  getHomePath() {
    const currentPath = window.location.pathname;

    // If we're in admin, employee, customer, or auth subdirectories, go back to root
    if (currentPath.includes('/admin/') || currentPath.includes('/employee/') || currentPath.includes('/customer/') || currentPath.includes('/auth/')) {
      return '../';
    }

    // If we're at root level, go to index
    return './';
  }

  /**
   * Update user information and re-render - Now uses UserContextService
   */
  updateUser(user = null) {
    // Sync with AuthGuard for backward compatibility
    this.authGuard.currentUser = user;
    this.authGuard.isAuthenticated = !!user;

    this.render();
  }

  /**
   * Refresh user bar from current token - Enhanced with token validation
   */
  async refresh() {
    // Check token validity first
    this.checkTokenValidity();

    // Reload user data if token is valid
    const token = localStorage.getItem("jwtToken");
    if (token && this.isValidToken(token)) {
      await this.loadUserFromToken();
      this.render();
    }
  }

  /**
   * Get current user information - Now uses UserContextService
   */
  getCurrentUser() {
    if (this.userContextService) {
      return this.userContextService.getCurrentUser();
    }

    // Fallback to AuthGuard
    return this.authGuard.currentUser;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    return this.authGuard.hasRole(role);
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles) {
    return this.authGuard.hasAnyRole(roles);
  }

  /**
   * Cleanup method for proper disposal
   */
  destroy() {
    this.stopTokenValidityChecks();
    this.initialized = false;

    if (this.container) {
      this.container.innerHTML = '';
    }
  }

  /**
   * Setup theme toggle button functionality (unified for both authenticated and theme-only modes)
   */
  setupThemeToggle(button) {
    // Ensure themeManager is available
    if (!window.themeManager) {
      console.warn('ThemeManager not available, theme toggle will not work');
      return;
    }

    const IconManager = window.IconManager || { get: () => '🌙' };

    // Update button content based on current theme
    const updateButton = () => {
      const isDark = window.themeManager.isDarkMode();
      const icon = isDark ? IconManager.get('sun', { size: 20 }) : IconManager.get('moon', { size: 20 });
      const label = isDark ? 'Light' : 'Dark';

      button.innerHTML = `${icon}<span class="btn__text">${label}</span>`;
      button.title = `Switch to ${isDark ? 'light' : 'dark'} mode`;
    };

    // Initial update
    updateButton();

    // Add click handler
    button.addEventListener('click', () => {
      console.log('Theme toggle clicked, current theme:', window.themeManager.currentTheme);
      window.themeManager.toggleTheme();
      console.log('Theme toggled to:', window.themeManager.currentTheme);
      updateButton();
    });

    // Listen for theme changes from other sources
    if (typeof window.themeManager.addThemeChangeCallback === 'function') {
      window.themeManager.addThemeChangeCallback(updateButton);
    }
  }

  /**
   * Render theme-only mode when userbar is not shown (user not authenticated)
   * Generic method that can be extended with additional features like sign-in
   */
  renderThemeOnlyMode() {
    console.log('🎨 UserBar: Rendering theme-only mode');

    // Ensure we have a container using the same structure as authenticated mode
    this.ensureContainer();

    if (!this.container) {
      console.warn('No container available for theme-only mode');
      return;
    }

    // Show the container and render unauthenticated userbar content
    this.container.style.display = '';

    // Ensure themeManager is available
    if (!window.themeManager) {
      console.warn('ThemeManager not available, theme toggle will not work');
      this.container.innerHTML = ''; // Clear container if no theme manager
      return;
    }

    // Import IconManager (it should be available globally)
    const IconManager = window.IconManager || { get: () => '🌙' };

    // Create the unauthenticated userbar content using existing utility classes
    // This can be easily extended with additional features like sign-in
    const isDark = window.themeManager.isDarkMode();
    const icon = isDark ? IconManager.get('sun', { size: 20 }) : IconManager.get('moon', { size: 20 });
    const label = isDark ? 'Light' : 'Dark';

    this.container.innerHTML = `
      <button id="userbar-theme-toggle" class="btn btn--ghost btn--sm btn--mobile-icon-only" title="Toggle theme">
        ${icon}
        <span class="btn__text">${label}</span>
      </button>
    `;

    // Setup the theme toggle functionality using the same method as authenticated users
    const themeToggle = document.getElementById('userbar-theme-toggle');
    if (themeToggle) {
      this.setupThemeToggle(themeToggle);
    }
  }

  /**
   * Comprehensive cleanup using lifecycle manager
   */
  cleanup() {
    this.lifecycle.lifecycleCleanup();
    this.destroy();
  }

}

// Create singleton instance
const userBarManager = new UserBarManager();

// Export the manager and convenience functions
export { userBarManager as UserBarManager };

// Named exports for specific functionality
export const renderUserBar = (user) => userBarManager.render(user || userBarManager.getCurrentUser());
export const updateUserBar = (user) => userBarManager.updateUser(user);
export const refreshUserBar = () => userBarManager.refresh();
export const getCurrentUser = () => userBarManager.getCurrentUser();

// Do NOT auto-initialize - let main.js handle initialization to prevent re-renders
// during navigation. UserBarManager is registered as critical component.

// Backward compatibility - attach to window for existing code
if (typeof window !== 'undefined') {
  window.UserBar = userBarManager;
  window.renderUserBar = renderUserBar;
  window.updateUserBar = updateUserBar;
  window.refreshUserBar = refreshUserBar;
  window.getCurrentUser = getCurrentUser;

  // Legacy function support
  window.userBarManager = userBarManager;
}

// Userbar styles are now handled by SCSS components/_userbar.scss
